import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, LogOut, Key, Ticket, Euro, FileText, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { providerApi, clearProviderToken } from '../../lib/providerApi';
import { formatPrice, DATA_SOURCE_LABELS } from '../../lib/utils';

const TABS = [
  { id: 'profile', label: 'Profil', icon: Building2 },
  { id: 'deals', label: 'Deals & Gutscheine', icon: Ticket },
  { id: 'prices', label: 'Preise', icon: Euro },
  { id: 'api', label: 'API-Key', icon: Key },
  { id: 'invoices', label: 'Rechnungen', icon: FileText },
];

export default function ProviderDashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({});
  const [deals, setDeals] = useState([]);
  const [dealForm, setDealForm] = useState({ code: '', title: '', type: 'coupon', value: '', tlds: '', description: '', end_date: '' });
  const [pricesText, setPricesText] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [apiKey, setApiKey] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const data = await providerApi.me();
      setMe(data);
      setProfileForm({
        company_name: data.company_name || '',
        contact_name: data.contact_name || '',
        address: data.address || '',
        vat_id: data.vat_id || '',
        description: data.provider?.description || '',
        website: data.provider?.website || '',
        affiliate_url: data.provider?.affiliate_url || '',
        pricing_url: '',
      });
      if (data.status === 'active' && data.provider?.tier === 'pro') {
        providerApi.listDeals().then(setDeals).catch(() => {});
        providerApi.listInvoices().then(setInvoices).catch(() => {});
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { clearProviderToken(); navigate('/provider/login'); };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await providerApi.updateProfile(profileForm);
      toast.success('Profil gespeichert');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveDeal = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...dealForm,
        value: parseFloat(dealForm.value),
        tlds: dealForm.tlds ? dealForm.tlds.split(',').map(s => s.trim()).filter(Boolean) : null,
        usage_limit: null,
      };
      await providerApi.createDeal(payload);
      toast.success('Deal erstellt');
      setDealForm({ code: '', title: '', type: 'coupon', value: '', tlds: '', description: '', end_date: '' });
      setDeals(await providerApi.listDeals());
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteDeal = async (id) => {
    try {
      await providerApi.deleteDeal(id);
      toast.success('Deal gelöscht');
      setDeals(await providerApi.listDeals());
    } catch (err) {
      toast.error(err.message);
    }
  };

  const savePrices = async (e) => {
    e.preventDefault();
    let prices;
    try {
      prices = JSON.parse(pricesText);
    } catch {
      toast.error('Ungültiges JSON. Format: [{ "tld": ".de", "registration": 0.49, "renewal": 8.99 }]');
      return;
    }
    try {
      const res = await providerApi.updatePrices(prices);
      toast.success(res.message || 'Preise aktualisiert');
      setPricesText('');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const genApiKey = async () => {
    try {
      const res = await providerApi.generateApiKey();
      setApiKey(res.api_key);
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-sky-600" /></div>;
  }

  const provider = me?.provider;
  const pending = me?.status !== 'active';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Provider-Dashboard
            </h1>
            <p className="text-sm text-muted-foreground">{me?.company_name || me?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={pending ? 'outline' : 'default'} className={pending ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}>
              {me?.status === 'active' ? 'Aktiv' : me?.status === 'claim_pending' ? 'Claim wird geprüft' : 'Freischaltung ausstehend'}
            </Badge>
            <Button variant="outline" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-1" /> Abmelden</Button>
          </div>
        </div>

        {pending && (
          <Card className="rounded-2xl mb-6 border-amber-200 bg-amber-50/50">
            <CardContent className="p-4 text-sm text-amber-900">
              Ihr Konto wird derzeit geprüft. Nach Freischaltung und Zahlungseingang können Sie Profil, Deals und
              Preise selbst verwalten und Ihre Preise täglich aktualisieren.
            </CardContent>
          </Card>
        )}

        {provider && (
          <Card className="rounded-2xl mb-6">
            <CardContent className="p-4 flex flex-wrap items-center gap-4">
              {provider.logo_url ? (
                <img src={provider.logo_url} alt="Logo" className="w-12 h-12 rounded-xl object-contain bg-white border" />
              ) : (
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center"><Building2 className="w-6 h-6 text-slate-500" /></div>
              )}
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{provider.name}</span>
                  {provider.tier === 'pro' && <Badge className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">Pro</Badge>}
                </div>
                <span className="text-xs text-muted-foreground">
                  {DATA_SOURCE_LABELS[provider.data_source]} · Zuletzt aktualisiert:{' '}
                  {provider.last_import_at ? new Date(provider.last_import_at).toLocaleDateString('de-DE') : '–'}
                </span>
              </div>
              <Link to="/" className="ml-auto text-xs text-sky-600 hover:text-sky-700">Vorschau ansehen →</Link>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {TABS.map(t => (
            <Button key={t.id} variant={tab === t.id ? 'default' : 'outline'} size="sm" onClick={() => setTab(t.id)}>
              <t.icon className="w-4 h-4 mr-1" /> {t.label}
            </Button>
          ))}
        </div>

        {/* PROFILE */}
        {tab === 'profile' && (
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Profil & Unternehmensdaten</h2>
              <form onSubmit={saveProfile} className="grid sm:grid-cols-2 gap-4">
                <div><Label>Firmenname</Label><Input value={profileForm.company_name} onChange={e => setProfileForm({...profileForm, company_name: e.target.value})} /></div>
                <div><Label>Ansprechpartner</Label><Input value={profileForm.contact_name} onChange={e => setProfileForm({...profileForm, contact_name: e.target.value})} /></div>
                <div><Label>Anschrift</Label><Input value={profileForm.address} onChange={e => setProfileForm({...profileForm, address: e.target.value})} /></div>
                <div><Label>USt-ID</Label><Input value={profileForm.vat_id} onChange={e => setProfileForm({...profileForm, vat_id: e.target.value})} /></div>
                <div className="sm:col-span-2"><Label>Beschreibung (öffentlich, nur Pro)</Label><Input value={profileForm.description} onChange={e => setProfileForm({...profileForm, description: e.target.value})} /></div>
                <div><Label>Website</Label><Input value={profileForm.website} onChange={e => setProfileForm({...profileForm, website: e.target.value})} /></div>
                <div><Label>Affiliate-Link (nur Pro)</Label><Input value={profileForm.affiliate_url} onChange={e => setProfileForm({...profileForm, affiliate_url: e.target.value})} /></div>
                <div className="sm:col-span-2"><Label>Preisliste als JSON-URL (Polling, optional)</Label><Input placeholder="https://ihre-domain.de/preise.json" value={profileForm.pricing_url} onChange={e => setProfileForm({...profileForm, pricing_url: e.target.value})} /></div>
                <div className="sm:col-span-2"><Button type="submit">Profil speichern</Button></div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* DEALS */}
        {tab === 'deals' && (
          <>
            {provider?.tier !== 'pro' ? (
              <Card className="rounded-2xl">
                <CardContent className="p-8 text-center text-sm text-muted-foreground">
                  Deals & Gutscheine sind ein Pro-Feature. Kontaktieren Sie uns für die Freischaltung.
                </CardContent>
              </Card>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                <Card className="rounded-2xl">
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-4">Neuer Deal</h2>
                    <form onSubmit={saveDeal} className="space-y-4">
                      <div><Label>Code</Label><Input required value={dealForm.code} onChange={e => setDealForm({...dealForm, code: e.target.value})} placeholder="DP10" /></div>
                      <div><Label>Titel</Label><Input required value={dealForm.title} onChange={e => setDealForm({...dealForm, title: e.target.value})} placeholder="10% Rabatt auf .de" /></div>
                      <div>
                        <Label>Typ</Label>
                        <select className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm" value={dealForm.type} onChange={e => setDealForm({...dealForm, type: e.target.value})}>
                          <option value="coupon">Gutschein (Code)</option>
                          <option value="fixed_percent">Prozent-Rabatt</option>
                          <option value="amount_off">Betrag-Rabatt</option>
                        </select>
                      </div>
                      <div><Label>Wert (z.B. 10 für 10%)</Label><Input required type="number" step="0.01" value={dealForm.value} onChange={e => setDealForm({...dealForm, value: e.target.value})} /></div>
                      <div><Label>TLDs (kommagetrennt, leer = alle)</Label><Input value={dealForm.tlds} onChange={e => setDealForm({...dealForm, tlds: e.target.value})} placeholder=".de, .com" /></div>
                      <div><Label>Beschreibung</Label><Input value={dealForm.description} onChange={e => setDealForm({...dealForm, description: e.target.value})} /></div>
                      <div><Label>Gültig bis (optional)</Label><Input type="date" value={dealForm.end_date} onChange={e => setDealForm({...dealForm, end_date: e.target.value})} /></div>
                      <Button type="submit">Deal erstellen</Button>
                    </form>
                  </CardContent>
                </Card>
                <div className="space-y-3">
                  <h2 className="text-lg font-semibold text-slate-900">Bestehende Deals</h2>
                  {deals.length === 0 && <p className="text-sm text-muted-foreground">Noch keine Deals angelegt.</p>}
                  {deals.map(d => (
                    <Card key={d.id} className="rounded-2xl">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm text-slate-900">{d.title} <Badge className="ml-1 text-[10px] bg-amber-50 text-amber-800 border-amber-200">{d.code}</Badge></p>
                          <p className="text-xs text-muted-foreground">
                            {d.type === 'fixed_percent' ? `${d.value}% Rabatt` : d.type === 'amount_off' ? `${formatPrice(d.value)} Rabatt` : 'Gutschein-Code'}
                            {d.tlds?.length ? ` · ${d.tlds.join(', ')}` : ' · alle TLDs'}
                            {d.end_date ? ` · bis ${d.end_date}` : ''}
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => deleteDeal(d.id)}>Löschen</Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* PRICES */}
        {tab === 'prices' && (
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Preise aktualisieren</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Tragen Sie Ihre Preise als JSON ein – sie werden sofort übernommen und in der Vergleichstabelle
                angezeigt (Quelle: „Anbieter-API").
              </p>
              <form onSubmit={savePrices} className="space-y-4">
                <div>
                  <Label>Preise (JSON)</Label>
                  <textarea
                    className="w-full min-h-[200px] rounded-md border border-input bg-white px-3 py-2 text-sm font-mono"
                    value={pricesText}
                    onChange={e => setPricesText(e.target.value)}
                    placeholder='[{"tld": ".de", "registration": 0.49, "renewal": 8.99, "transfer": 0.0}]'
                  />
                </div>
                <Button type="submit">Preise übernehmen</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* API KEY */}
        {tab === 'api' && (
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-2">API-Key (Push-API)</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Mit Ihrem API-Key können Sie Ihre Preise aktiv an uns senden:{' '}
                <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">POST /api/v1/providers/prices</code>
              </p>
              {apiKey ? (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                  <p className="text-xs text-slate-500 mb-1">Ihr neuer API-Key (nur einmal sichtbar):</p>
                  <code className="text-sm break-all">{apiKey}</code>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-4">
                  {me?.has_api_key ? 'Sie haben bereits einen API-Key. Bei Verlust können Sie einen neuen generieren (der alte wird ungültig).' : 'Sie haben noch keinen API-Key.'}
                </p>
              )}
              <Button onClick={genApiKey}>{apiKey ? 'Neuen Key generieren' : 'API-Key generieren'}</Button>
            </CardContent>
          </Card>
        )}

        {/* INVOICES */}
        {tab === 'invoices' && (
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Rechnungen</h2>
              {invoices.length === 0 ? (
                <p className="text-sm text-muted-foreground">Noch keine Rechnungen vorhanden.</p>
              ) : (
                <div className="space-y-2">
                  {invoices.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{inv.number}</p>
                        <p className="text-xs text-muted-foreground">{inv.description || 'Jahresabo Pro'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">{formatPrice(inv.amount_net + (inv.amount_net * (inv.vat_percent || 0) / 100))}</p>
                        <Badge variant="outline" className="text-[10px]">{inv.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
